import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Trans } from '@lingui/react/macro';
import { Accordion, Checkbox, CheckboxGroup } from '@ygtechnologies/rubus-ui';

import { IState } from '@/state/IState';
import { useOptionsFilter } from '../useOptionsFilter';
import type { OptionsFilterFacets } from '../useOptionsData';
import { buildTopLevelCategoryMap } from '@/utils/categories';
import theme from './FilterSidebar.module.scss';

interface Props {
    facets: OptionsFilterFacets;
}

export const CategoryFilter: React.FC<Props> = ({ facets }) => {
    const { filter, setCategories } = useOptionsFilter();
    const itemCategories = useSelector((state: IState) => state.global.itemCategories);

    const filteredItemCategories = useMemo(() => {
        const topLevelMap = buildTopLevelCategoryMap(itemCategories);
        const topLevelIds = new Set<number>();
        for (const facetId of facets.itemCategoryIds) {
            const topId = topLevelMap.get(facetId);
            if (topId != null) topLevelIds.add(topId);
        }
        return itemCategories.filter((ic) => topLevelIds.has(ic.id));
    }, [facets.itemCategoryIds, itemCategories]);

    if (filteredItemCategories.length === 0) return null;

    return (
        <Accordion
            variant="elevated"
            defaultOpen
            title={
                <span className={theme.filterTitle}>
                    <Trans>Category filters</Trans>
                </span>
            }>
            <div className={theme.filterBody}>
                <CheckboxGroup>
                    {filteredItemCategories.map((ic) => {
                        const isChecked = filter.categories.includes(ic.id);
                        const onChange = () =>
                            setCategories(
                                isChecked ? filter.categories.filter((id) => id !== ic.id) : [...filter.categories, ic.id]
                            );
                        return (
                            <Checkbox key={ic.id} inputSize="md" checked={isChecked} onChange={onChange}>
                                {ic.name}
                            </Checkbox>
                        );
                    })}
                </CheckboxGroup>
            </div>
        </Accordion>
    );
};
